/**
 * Internal Sync-Config Toggle API Contract (SCH-447, B1)
 *
 * POST /internal/platform-data-sync/sync-config  — enable/disable ONE sync_type
 *                                                   across a LIST of schools
 * GET  /internal/platform-data-sync/sync-config   — read current is_enabled + audit
 *                                                   per school for one sync_type
 *
 * API-key-gated (`INTERNAL_API_KEY`). This toggles the per-school
 * `PlatformDataSyncConfig.is_enabled` flag, which gates BOTH sync lanes:
 *   - SCHEDULED: the staleness scheduler's `enabledOnly` filter excludes a
 *     config whose `is_enabled = false`, so it never enqueues due jobs for it.
 *   - MANUAL: `createAndProcessSyncJob` (the `run-job` path) returns
 *     `SYNC_CONFIG_DISABLED` for a disabled config.
 *
 * This is DISTINCT from Autopilot (`SystemConfiguration` PLATFORM_SYNC_SETTINGS/
 * AUTOPILOT — a school-wide scheduled-lane gate) and from
 * `School.platformSyncEnabled` (the master kill-switch). It is the per-(school,
 * sync_type) on/off switch — the same flag the Clerk-gated single PATCH
 * (`/v1/schools/:id/data-sync-configs/:job_id`) writes.
 *
 * Audit trail: disabling records `disabled_reason` / `disabled_reason_code` /
 * `disabled_at` / `disabled_by` on the config row; enabling clears them. `last_platform_config_update` is bumped
 * on every write. The GET surfaces these back.
 *
 * BULK SEMANTICS: the POST is per-school best-effort. An unresolvable school
 * (`resolved: false`) or a school with no non-archived config for that sync_type
 * (`found: false`) is a per-school outcome, NOT a batch-level failure — the call
 * still returns HTTP 200. Only a malformed body (e.g. `enabled: false` with no
 * `reason` or no `disabled_reason_code`) is a 400.
 */

import { z } from 'zod'

/**
 * Why a sync config is disabled, as a value a DETECTOR can act on (SCH-2012).
 *
 * `disabled_reason` is free text and always has been, so it mixes intents that
 * read identically to a machine: a seasonal pause, an incident, a fleet-wide
 * retirement and "this school does not use this module" are all just prose. Only
 * `NOT_USED` and `RETIRED` let a feature-health row stay quiet; every other
 * value — and a NULL — is a state we expect to end, so the row reads `unknown`.
 */
export const DisabledReasonCodeSchema = z.enum(['NOT_USED', 'INCIDENT', 'SEASONAL', 'RETIRED', 'NOT_YET_ENABLED'])

export type DisabledReasonCodeValue = z.infer<typeof DisabledReasonCodeSchema>

/**
 * Audit fields surfaced from a `PlatformDataSyncConfig` row.
 */
export const SyncConfigAuditSchema = z.object({
  disabledReason: z.string().nullable().describe('Reason recorded when the config was disabled'),
  disabledReasonCode: DisabledReasonCodeSchema.nullable().describe(
    'Machine-readable intent behind the disable; null means not yet classified'
  ),
  disabledAt: z.string().nullable().describe('ISO timestamp the config was disabled'),
  disabledBy: z.string().nullable().describe('Identity that disabled the config'),
  lastPlatformConfigUpdate: z.string().nullable().describe('ISO timestamp of the last platform-config write')
})

export type SyncConfigAudit = z.infer<typeof SyncConfigAuditSchema>

/**
 * The values a caller expects one school's config row to still hold when its
 * write lands (SCH-5098). The write and this check are one statement: a row that
 * no longer holds them is not written, and the result reports `stale`.
 */
export const SyncConfigExpectedPriorSchema = z.object({
  enabled: z.boolean().describe('Expected current is_enabled'),
  disabled_reason: z.string().nullable().describe('Expected current disabled_reason'),
  disabled_reason_code: DisabledReasonCodeSchema.nullable().describe('Expected current disabled_reason_code')
})

export type SyncConfigExpectedPrior = z.infer<typeof SyncConfigExpectedPriorSchema>

/**
 * POST request body. Toggles `enabled` for ONE `sync_type` across a LIST of
 * schools. `reason` is REQUIRED when `enabled = false` (mirrors the Clerk
 * single-toggle contract). `changed_by` defaults to `'internal-api'`.
 *
 * `expected_prior` (optional) makes each school's write conditional: keyed by
 * the school id exactly as given in `school_ids`, it must name every school and
 * no other. Callers that omit it behave exactly as before.
 */
export const SetSyncConfigEnabledRequestSchema = z
  .object({
    sync_type: z.string().min(1).describe('Sync type to toggle (e.g. SECTION_ROSTER, CALENDAR_EVENTS)'),
    school_ids: z
      .array(z.string().min(1))
      .min(1)
      .describe('Schools to apply the toggle to (each a public ID (CUID) or slug)'),
    enabled: z.boolean().describe('New is_enabled state for the (school, sync_type) configs'),
    reason: z.string().max(2000).optional().describe('Audit reason; REQUIRED when enabled is false'),
    disabled_reason_code: DisabledReasonCodeSchema.optional().describe(
      'Machine-readable intent; REQUIRED when enabled is false'
    ),
    changed_by: z
      .string()
      .max(200)
      .optional()
      .describe("Optional caller identity for the audit trail (defaults to 'internal-api')"),
    expected_prior: z
      .record(z.string().min(1), SyncConfigExpectedPriorSchema)
      .optional()
      .describe(
        'Per school (keyed as in school_ids): write a school only while its row still holds these values; otherwise do not write and report `stale`'
      )
  })
  .refine(data => data.enabled || (data.reason !== undefined && data.reason.trim().length > 0), {
    message: 'reason is required when disabling a sync configuration',
    path: ['reason']
  })
  // The code, not the prose, is what a detector reads. Without this refine the
  // column re-rots into "mostly null" one convenient disable at a time, and a
  // null code is exactly what makes feature health say `unknown` instead of
  // `n/a` — so an unclassified disable is loud rather than wrong.
  .refine(data => data.enabled || data.disabled_reason_code !== undefined, {
    message: 'disabled_reason_code is required when disabling a sync configuration',
    path: ['disabled_reason_code']
  })
  .refine(data => expectedPriorCoversSchools(data.school_ids, data.expected_prior), {
    message: 'expected_prior must name every school in school_ids and no other',
    path: ['expected_prior']
  })

/** True when `expected` is absent, or its keys are exactly the schools in `schoolIds`. */
function expectedPriorCoversSchools(schoolIds: string[], expected: Record<string, unknown> | undefined): boolean {
  if (expected === undefined) return true
  const schools = new Set(schoolIds)
  const keys = Object.keys(expected)
  return schoolIds.every(id => keys.includes(id)) && keys.every(key => schools.has(key))
}

export type SetSyncConfigEnabledRequest = z.infer<typeof SetSyncConfigEnabledRequestSchema>

/**
 * Per-school result of a POST toggle.
 *  - `resolved: false` → the school_id didn't resolve to a school row.
 *  - `found: false`    → the school resolved but has no non-archived config for
 *                        that sync_type (nothing was written).
 *  - `found: true`     → the config was toggled; `previousEnabled`/`newEnabled`
 *                        reflect the change.
 */
export const SyncConfigToggleResultSchema = z.object({
  schoolId: z.string().describe('The school_id as supplied in the request (public id or slug)'),
  resolvedSchoolId: z.string().nullable().describe("The resolved school's public ID, or null if unresolved"),
  resolved: z.boolean().describe('Whether the school_id resolved to a school'),
  found: z.boolean().describe('Whether a non-archived config existed for the (school, sync_type)'),
  previousEnabled: z.boolean().nullable().describe('is_enabled before the write (null when not found)'),
  newEnabled: z.boolean().nullable().describe('is_enabled after the write (null when not found)'),
  audit: SyncConfigAuditSchema.nullable().describe('Post-write audit fields (null when not found)'),
  stale: z
    .boolean()
    .optional()
    .describe(
      'Present only when expected_prior was sent. True when it no longer matched, so nothing was written (previousEnabled, newEnabled and audit are then the CURRENT values); false when it matched and the write landed'
    )
})

export type SyncConfigToggleResult = z.infer<typeof SyncConfigToggleResultSchema>

/**
 * POST response. Top-level counts aggregate the per-school results.
 */
export const SetSyncConfigEnabledResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    syncType: z.string().describe('The sync type that was toggled'),
    enabled: z.boolean().describe('The requested target state'),
    totalRequested: z.number().int().describe('Number of school_ids in the request'),
    resolved: z.number().int().describe('How many school_ids resolved to a school'),
    found: z.number().int().describe('How many resolved schools had a non-archived config (and were written)'),
    changed: z.number().int().describe('How many configs actually changed state (previousEnabled !== newEnabled)'),
    results: z.array(SyncConfigToggleResultSchema)
  })
})

export type SetSyncConfigEnabledResponse = z.infer<typeof SetSyncConfigEnabledResponseSchema>

/**
 * GET request (query). `sync_type` is required; `school_id` may be repeated to
 * read several schools in one call.
 */
export const GetSyncConfigRequestSchema = z.object({
  sync_type: z.string().min(1).describe('Sync type to read'),
  school_id: z
    .array(z.string().min(1))
    .min(1)
    .describe('Schools to read (each a public ID (CUID) or slug; repeat the param for several)')
})

export type GetSyncConfigRequest = z.infer<typeof GetSyncConfigRequestSchema>

/**
 * Per-school read result.
 */
export const SyncConfigReadResultSchema = z.object({
  schoolId: z.string().describe('The school_id as supplied in the request'),
  resolvedSchoolId: z.string().nullable().describe("The resolved school's public ID, or null if unresolved"),
  resolved: z.boolean().describe('Whether the school_id resolved to a school'),
  found: z.boolean().describe('Whether a non-archived config existed for the (school, sync_type)'),
  isEnabled: z.boolean().nullable().describe('Current is_enabled (null when not found)'),
  audit: SyncConfigAuditSchema.nullable().describe('Current audit fields (null when not found)')
})

export type SyncConfigReadResult = z.infer<typeof SyncConfigReadResultSchema>

/**
 * GET response.
 */
export const GetSyncConfigResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    syncType: z.string(),
    results: z.array(SyncConfigReadResultSchema)
  })
})

export type GetSyncConfigResponse = z.infer<typeof GetSyncConfigResponseSchema>
